import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaDownload, FaSync } from "react-icons/fa";
import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
  DataTable,
  UserInfo,
  Filters,
  FilterInput,
  OrderStatusBadge,
  DataTableScroll,
  TableHeader,
  TableRow,
  TableCell,
  LoadingCell,
  OrderNumber,
  OrderId,
  UserName,
  UserEmail,
  CourseItem,
  DateCell,
  TimeCell,
  PaginationContainer,
  PaginationInfo,
  PaginationControls,
  PageNumber,
  PaginationButton,
} from "./AdminOrders.styles";
import { toast } from "sonner";
import { getAllOrders } from "../../../store/slices/admin/asyncThunks";
import {
  selectOrders,
  selectOrdersLoading,
  selectOrderFilters,
  selectOrdersPagination,
  setOrderFilters,
} from "../../../store/slices/admin/slice";
import {
  convertOrdersToCSV,
  downloadCSV,
  priceFormatter,
} from "../../../utils/utils";
import CustomDateFilter from "../../../components/CustomDateFilter/CustomDateFilter";
import CustomSelect from "../../../components/utils/CustomSelect";
import { exportOrders } from "../../../store/slices/orders/asyncThunks";
import { useMediaQuery } from "react-responsive";

const AdminOrders = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux selectors
  const orders = useSelector(selectOrders);
  const ordersLoading = useSelector(selectOrdersLoading);
  const orderFilters = useSelector(selectOrderFilters);
  const ordersPagination = useSelector(selectOrdersPagination);

  // Local state
  const [orderSearchTimeout, setOrderSearchTimeout] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const isSmallScreen = useMediaQuery({ maxWidth: 825 });

  // Load data on component mount and when timeframe changes
  useEffect(() => {
    dispatch(getAllOrders(orderFilters));
  }, [dispatch, orderFilters]);

  // Clear pending debounce timer on unmount
  useEffect(() => {
    return () => {
      if (orderSearchTimeout) clearTimeout(orderSearchTimeout);
    };
  }, [orderSearchTimeout]);

  const refreshData = async () => {
    setIsLoading(true);
    try {
      await dispatch(getAllOrders(orderFilters)).unwrap();

      toast.success("Podaci su osveženi!");
    } catch (error) {
      toast.error("Greška pri osvežavanju podataka");
    } finally {
      setIsLoading(false);
    }
  };

  const handleOrderFiltersChange = (filterUpdates) => {
    dispatch(setOrderFilters(filterUpdates));
  };

  // Debounced search to avoid too many API calls
  const handleOrderSearchChange = (value) => {
    if (orderSearchTimeout) {
      clearTimeout(orderSearchTimeout);
    }

    const timeout = setTimeout(() => {
      dispatch(setOrderFilters({ search: value, page: 1 }));
    }, 500); // Wait 500ms after user stops typing

    setOrderSearchTimeout(timeout);
  };

  const handleOrderPageChange = (newPage) => {
    dispatch(setOrderFilters({ page: newPage }));
  };

  const handleExportOrders = async (exportAll = true) => {
    setIsLoading(true);

    // Choose filters based on export type
    const exportFilters = exportAll
      ? {} // No filters = export ALL orders
      : {
          search: orderFilters.search || "",
          dateFrom: orderFilters.dateFrom || "",
          dateTo: orderFilters.dateTo || "",
          status:
            orderFilters.status && orderFilters.status !== "all"
              ? orderFilters.status
              : "",
        }; // Use current filters

    dispatch(exportOrders(exportFilters))
      .unwrap()
      .then((result) => {
        if (!result) {
          throw new Error("Prazan odgovor sa servera");
        }

        if (!result.orders) {
          throw new Error("Nedostaju orders u odgovoru sa servera");
        }

        if (!Array.isArray(result.orders)) {
          throw new Error("Orders nije array");
        }

        const csvData = convertOrdersToCSV(result.orders);
        const filename = exportAll
          ? `sve-porudzbine-${new Date().toISOString().split("T")[0]}.csv`
          : `filtrirane-porudzbine-${new Date().toISOString().split("T")[0]}.csv`;
        downloadCSV(csvData, filename);

        toast.success(`Izvezeno ${result.total} porudžbina`);
      })
      .catch((err) => {
        console.error("❌ Frontend Export error:", err);
        toast.error(err.message || "Greška pri izvozu");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOrderAction = (action, order) => {
    switch (action) {
      case "view":
        navigate(`/admin-panel/porudzbine/${order.id}`);
        break;
      default:
        break;
    }
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Pregled Porudžbina</SectionTitle>
        <SectionActions>
          <Button
            $secondary
            onClick={() => handleExportOrders(true)}
            disabled={isLoading}
          >
            <FaDownload />
            Izvezi SVE
          </Button>
          {(orderFilters.search ||
            orderFilters.dateFrom ||
            orderFilters.dateTo ||
            (orderFilters.status && orderFilters.status !== "completed")) && (
            <Button
              $secondary
              onClick={() => handleExportOrders(false)}
              disabled={isLoading}
            >
              <FaDownload />
              Izvezi FILTRIRANE
            </Button>
          )}
          <Button
            $secondary
            onClick={refreshData}
            disabled={isLoading}
            $loading={isLoading}
          >
            <FaSync />
            Osveži
          </Button>
        </SectionActions>
      </SectionHeader>

      <Filters>
        <FilterInput
          placeholder="Pretraži po broju porudžbine, imenu ili email-u..."
          defaultValue={orderFilters.search}
          onChange={(e) => handleOrderSearchChange(e.target.value)}
        />

        <CustomDateFilter
          dateFrom={orderFilters.dateFrom}
          dateTo={orderFilters.dateTo}
          onDateChange={(dateFrom, dateTo) =>
            handleOrderFiltersChange({ dateFrom, dateTo, page: 1 })
          }
          placeholder="Filtriraj po datumu"
        />

        <CustomSelect
          $small
          value={orderFilters.status || "completed"}
          onChange={(status) => handleOrderFiltersChange({ status, page: 1 })}
          options={[
            { value: "completed", label: "Završene" },
            { value: "pending", label: "Na čekanju" },
            { value: "processing", label: "U obradi" },
            { value: "canceled", label: "Otkazane" },
            { value: "all", label: "Svi statusi" },
          ]}
          ariaLabel="Filtriraj po statusu porudžbine"
        />
      </Filters>

      <DataTableScroll>
        <DataTable>
          <TableHeader $align="flex-start">Broj Porudžbine</TableHeader>
          <TableHeader>Korisnik</TableHeader>
          <TableHeader>Kursevi</TableHeader>
          <TableHeader>Iznos</TableHeader>
          <TableHeader>Datum</TableHeader>

          {ordersLoading ? (
            <LoadingCell>
              <l-line-spinner
                size="24"
                stroke="3"
                speed="1"
                color="var(--accent)"
              />
            </LoadingCell>
          ) : orders.length > 0 ? (
            orders.map((order, index) => (
              <TableRow
                key={order.id}
                $index={index}
                onClick={() => handleOrderAction("view", order)}
              >
                <TableCell>
                  <div>
                    <OrderNumber>{order.orderNumber}</OrderNumber>
                    <OrderId>ID: #{order.id.slice(0, 8)}</OrderId>
                    {orderFilters.status !== "completed" && (
                      <OrderStatusBadge $status={order.status}>
                        {{
                          completed: "Završena",
                          pending: "Na čekanju",
                          processing: "U obradi",
                          canceled: "Otkazana",
                        }[order.status] || order.status}
                      </OrderStatusBadge>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <UserInfo>
                    <div>
                      <UserName>
                        {order.firstName} {order.lastName}
                      </UserName>
                      <UserEmail>{order.email}</UserEmail>
                    </div>
                  </UserInfo>
                </TableCell>
                <TableCell style={{ whiteSpace: "normal" }}>
                  {order.items?.length > 0 ? (
                    <div>
                      {order.items.map((item, idx) => (
                        <CourseItem
                          key={idx}
                          $hasMargin={idx < order.items.length - 1}
                        >
                          {item.courseTitle}
                        </CourseItem>
                      ))}
                    </div>
                  ) : (
                    <CourseItem $empty>Nema stavki</CourseItem>
                  )}
                </TableCell>
                <TableCell>{priceFormatter(order.total)} RSD</TableCell>
                <TableCell>
                  <div>
                    <DateCell>
                      {new Date(order.createdAt).toLocaleDateString(
                        "sr-latn-RS",
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        },
                      )}
                    </DateCell>
                    <TimeCell>
                      {new Date(order.createdAt).toLocaleTimeString(
                        "sr-latn-RS",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </TimeCell>
                  </div>
                </TableCell>
                {/* <TableCell style={{ justifyContent: "center" }}>
                  <ActionButtons>
                    <ActionBtn
                      $type="view"
                      onClick={() => handleOrderAction("view", order)}
                      title="Pogledaj detalje porudžbine"
                    >
                      <FaEye />
                    </ActionBtn>
                  </ActionButtons>
                </TableCell> */}
              </TableRow>
            ))
          ) : (
            <LoadingCell>
              {orderFilters.search
                ? "Nema porudžbina koje odgovaraju pretrazi"
                : "Nema dostupnih porudžbina"}
            </LoadingCell>
          )}
        </DataTable>
      </DataTableScroll>

      {ordersPagination && ordersPagination.totalPages > 1 && (
        <PaginationContainer>
          <PaginationInfo>
            Prikazuje se{" "}
            <strong>
              {(ordersPagination.currentPage - 1) * orderFilters.limit + 1}
            </strong>{" "}
            -{" "}
            <strong>
              {Math.min(
                ordersPagination.currentPage * orderFilters.limit,
                ordersPagination.totalOrders,
              )}
            </strong>{" "}
            od <strong>{ordersPagination.totalOrders}</strong> porudžbina
          </PaginationInfo>
          <PaginationControls>
            {isSmallScreen ? (
              <PaginationButton
                $secondary
                $small
                disabled={!ordersPagination.hasPrevPage}
                onClick={() =>
                  handleOrderPageChange(ordersPagination.currentPage - 1)
                }
              >
                ←
              </PaginationButton>
            ) : (
              <PaginationButton
                $secondary
                $small
                disabled={!ordersPagination.hasPrevPage}
                onClick={() =>
                  handleOrderPageChange(ordersPagination.currentPage - 1)
                }
              >
                ← Prethodna
              </PaginationButton>
            )}

            <div style={{ display: "flex", gap: "4px", margin: "0 10px" }}>
              {[...Array(Math.min(5, ordersPagination.totalPages))].map(
                (_, index) => {
                  let pageNum;
                  if (ordersPagination.totalPages <= 5) {
                    pageNum = index + 1;
                  } else if (ordersPagination.currentPage <= 3) {
                    pageNum = index + 1;
                  } else if (
                    ordersPagination.currentPage >=
                    ordersPagination.totalPages - 2
                  ) {
                    pageNum = ordersPagination.totalPages - 4 + index;
                  } else {
                    pageNum = ordersPagination.currentPage - 2 + index;
                  }

                  return (
                    <PageNumber
                      key={pageNum}
                      onClick={() => handleOrderPageChange(pageNum)}
                      $active={pageNum === ordersPagination.currentPage}
                    >
                      {pageNum}
                    </PageNumber>
                  );
                },
              )}
            </div>
            {isSmallScreen ? (
              <PaginationButton
                $secondary
                $small
                disabled={!ordersPagination.hasNextPage}
                onClick={() =>
                  handleOrderPageChange(ordersPagination.currentPage + 1)
                }
              >
                →
              </PaginationButton>
            ) : (
              <PaginationButton
                $secondary
                $small
                disabled={!ordersPagination.hasNextPage}
                onClick={() =>
                  handleOrderPageChange(ordersPagination.currentPage + 1)
                }
              >
                Sledeća →
              </PaginationButton>
            )}
          </PaginationControls>
        </PaginationContainer>
      )}
    </>
  );
};

export default AdminOrders;
